import { BOOK_FILTER_ON, type BookFilterParam } from '@meridian/contracts';

import styles from './book-filter.module.css';

import type { JSX } from 'react';

/**
 * Manual-book filter toggle (ADR-0042 · #84).
 *
 * A separate control from `UniverseFilter`, not a fifth key inside it, for the
 * same reason `BOOK` is not a `FilterKey`: ownership is an ORTHOGONAL axis.
 * `?idx=SPX&book=1` is "my S&P names", and folding the two into one segmented
 * control would make that combination unexpressible on screen even though the
 * URL and the API both support it.
 *
 * A toggle rather than a two-button group: the off state is the ABSENCE of the
 * param, so there is no second value to press.
 */
export interface BookFilterProps {
  readonly value: BookFilterParam | undefined;
  readonly onChange: (value: BookFilterParam | undefined) => void;
  /**
   * Lots in the book. Disables the control at zero — a filter that can only
   * ever return nothing is a trap, and the count says WHY it is unavailable
   * rather than leaving a dead button.
   */
  readonly lotCount: number;
}

export function BookFilter({ value, onChange, lotCount }: BookFilterProps): JSX.Element {
  const on = value === BOOK_FILTER_ON;
  const empty = lotCount === 0;
  return (
    <button
      type="button"
      className={styles['btn']}
      aria-pressed={on}
      disabled={empty}
      title={empty ? 'No book configured' : `${String(lotCount)} holdings`}
      onClick={() => {
        onChange(on ? undefined : BOOK_FILTER_ON);
      }}
    >
      MY BOOK
      {/* The count is the affordance that makes an empty book legible as
          "nothing configured" rather than "filter is broken". */}
      {!empty && <span className={styles['count']}>{lotCount}</span>}
    </button>
  );
}
